import {
  getCookie,
  decryptEncryptedJWT
} from 'src/common/config/FunctionHelper';

/**
 * Callback function signature for handling Server-Sent Events (SSE) data.
 *
 * @callback SSEEventHandler
 * @param {any} data - The parsed JSON payload or raw text received from the SSE stream.
 * @param {MessageEvent} event - The underlying native browser `MessageEvent` instance.
 */
export type SSEEventHandler = (data: any, event: MessageEvent) => void;

/**
 * Represents a registered subscriber within the SSE service.
 *
 * @interface Subscriber
 * @property {string} id - Unique identifier for the subscriber (e.g. `'app'`, `'orbit-ai'`).
 * @property {string | number} [siteId] - The specific site ID associated with this subscription.
 * @property {Record<string, SSEEventHandler>} eventHandlers - Map of SSE event names (e.g. `'agent-update'`) to their corresponding handler callbacks.
 */
interface Subscriber {
  id: string;
  siteId?: string | number;
  eventHandlers: Record<string, SSEEventHandler>;
}

/**
 * Central singleton SSE (Server-Sent Events) manager.
 *
 * Coordinates and maintains a single, shared `EventSource` connection to `/api/v2/stream`
 * across the entire application (including App-level listeners and Orbit AI components).
 *
 * ### Key Responsibilities:
 * - **Connection Deduplication**: Prevents multiple parallel HTTP connections when multiple
 *   components mount or subscribe simultaneously (e.g. `App.tsx` and `AgentsContainer.tsx` on `/dashboard`).
 * - **Event Demultiplexing**: Dispatches stream events (`site-update`, `keyword-update`, `ai-visibility-update`, `agent-update`)
 *   to matching subscriber callbacks.
 * - **Zero-Downtime Route Transitions**: Employs a 500ms grace period on component unmount so client-side
 *   route navigation across Orbit AI pages does not unnecessarily drop and reconnect the stream.
 * - **Automatic Exponential Backoff Reconnection**: Seamlessly reconnects with exponential backoff on network errors.
 * - **Dynamic Site Switching**: Automatically reconnects to the stream with updated parameters when `siteId` changes.
 */
class SSEManager {
  /** The active native EventSource instance, or `null` if disconnected. */
  private eventSource: EventSource | null = null;

  /** Unique composite key (`${userId}-${adminId}-${siteId}`) identifying the active connection parameters. */
  private connectionKey: string | null = null;

  /** Reference to the scheduled exponential backoff reconnection timeout. */
  private reconnectTimeout: NodeJS.Timeout | null = null;

  /** Reference to the scheduled disconnect grace period timeout on unmount. */
  private disconnectTimeout: NodeJS.Timeout | null = null;

  /** Current count of consecutive failed reconnection attempts. */
  private reconnectAttempts = 0;

  /** Registry of currently active subscribers, mapped by unique subscriber ID. */
  private subscribers = new Map<string, Subscriber>();

  /** Maximum reconnection delay in milliseconds (30 seconds). */
  private readonly MAX_RECONNECT_DELAY = 30000;

  /** Initial base reconnection delay in milliseconds (2 seconds). */
  private readonly INITIAL_RECONNECT_DELAY = 2000;

  /**
   * Calculates the exponential backoff reconnection delay based on the current attempt count.
   *
   * @private
   * @returns {number} The delay in milliseconds before the next reconnect attempt.
   */
  private getReconnectDelay(): number {
    const delay = Math.min(
      this.INITIAL_RECONNECT_DELAY * Math.pow(2, this.reconnectAttempts),
      this.MAX_RECONNECT_DELAY
    );
    this.reconnectAttempts++;
    return delay;
  }

  /**
   * Registers a subscriber and its event handlers, establishing or reusing the SSE connection.
   *
   * If a connection matching the user and site is already active, the new subscriber
   * is attached immediately without opening a duplicate HTTP connection.
   *
   * @param {string} id - A unique identifier for the subscriber (e.g. `'app'`, `'orbit-ai'`).
   * @param {Object} params - Subscription configuration.
   * @param {string | number} [params.siteId] - The active site ID for the connection.
   * @param {Record<string, SSEEventHandler>} params.eventHandlers - Map of event names to handler callbacks.
   *
   * @example
   * ```ts
   * sseService.subscribe('orbit-ai', {
   *   siteId: '12345',
   *   eventHandlers: {
   *     'agent-update': (data) => console.log('Agent updated:', data)
   *   }
   * });
   * ```
   */
  public subscribe(
    id: string,
    params: {
      siteId?: string | number;
      eventHandlers: Record<string, SSEEventHandler>;
    }
  ): void {
    // Cancel any pending disconnect grace period from a recent unmount
    if (this.disconnectTimeout) {
      clearTimeout(this.disconnectTimeout);
      this.disconnectTimeout = null;
    }

    // Detect if the siteId changed vs what's currently registered for this
    // subscriber — if so, the EventSource URL must be refreshed with the
    // new siteId query param, otherwise the backend continues filtering
    // keyword-update / ai-visibility-update against the stale siteId.
    const previousSub = this.subscribers.get(id);
    const siteIdChanged =
      previousSub != null &&
      String(previousSub.siteId || '') !== String(params.siteId || '');

    this.subscribers.set(id, {
      id,
      siteId: params.siteId,
      eventHandlers: params.eventHandlers
    });

    this.ensureConnection(siteIdChanged);
  }

  /**
   * Unregisters a subscriber by its unique ID.
   *
   * When all subscribers are removed (subscriber count reaches 0), a 500ms grace period
   * is started before closing the connection, allowing seamless transitions between pages.
   *
   * @param {string} id - The unique identifier of the subscriber to remove.
   *
   * @example
   * ```ts
   * sseService.unsubscribe('orbit-ai');
   * ```
   */
  public unsubscribe(id: string): void {
    this.subscribers.delete(id);

    if (this.subscribers.size === 0) {
      // 500ms grace period before closing to allow seamless transitions between routes
      if (this.disconnectTimeout) {
        clearTimeout(this.disconnectTimeout);
      }
      this.disconnectTimeout = setTimeout(() => {
        this.disconnect();
      }, 500);
    }
  }

  /**
   * Ensures that a single active EventSource connection is established and healthy.
   *
   * - Retrieves authentication JWT and extracts `userId` and `adminId`.
   * - Computes the connection key based on `userId`, `adminId`, and active `siteId`.
   * - If an existing connection is already active with the same parameters, no new connection is opened.
   * - If parameters have changed, gracefully closes the old connection and reconnects.
   *
   * @param {boolean} [forceReconnect=false] - Whether to force a new connection even if keys match.
   * @returns {Promise<void>}
   */
  public async ensureConnection(forceReconnect = false): Promise<void> {
    if (this.subscribers.size === 0) return;

    try {
      const encodedJWT = getCookie(process.env.REACT_APP_COOKIE_NAME);
      const jwtDecode: any = await decryptEncryptedJWT(encodedJWT);
      if (!jwtDecode || !jwtDecode.userId) return;

      const userId = jwtDecode.userId;
      const adminId = jwtDecode.adminId || '';

      // Determine active siteId from registered subscribers
      let activeSiteId = '';
      for (const sub of this.subscribers.values()) {
        if (sub.siteId) {
          activeSiteId = String(sub.siteId);
          break;
        }
      }

      const targetKey = `${userId}-${adminId}-${activeSiteId}`;

      // If connection is already active with matching key and not forced, reuse it
      if (
        !forceReconnect &&
        this.eventSource &&
        this.eventSource.readyState !== EventSource.CLOSED &&
        this.connectionKey === targetKey
      ) {
        return;
      }

      // Close previous connection if site or user changed
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
      }

      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }

      this.connectionKey = targetKey;

      const searchParams = new URLSearchParams({
        userId,
        adminId,
        siteId: activeSiteId
      });

      const eventSource = new EventSource(
        `${process.env.REACT_APP_API_URL}/api/v2/stream?${searchParams.toString()}`
      );

      this.eventSource = eventSource;

      eventSource.addEventListener('connected', () => {
        console.log('SSE connected successfully');
        this.reconnectAttempts = 0;
      });

      // Attach dispatchers for all stream events
      const knownEvents = [
        'site-update',
        'keyword-update',
        'ai-visibility-update',
        'agent-update'
      ];

      knownEvents.forEach((eventName) => {
        eventSource.addEventListener(eventName, (event) => {
          this.broadcast(eventName, event);
        });
      });

      eventSource.onerror = () => {
        eventSource.close();
        if (this.eventSource === eventSource) {
          this.eventSource = null;
        }

        if (this.subscribers.size > 0) {
          const delay = this.getReconnectDelay();
          this.reconnectTimeout = setTimeout(() => {
            this.ensureConnection(true);
          }, delay);
        }
      };
    } catch (err) {
      console.error('Error in SSE connection:', err);
    }
  }

  /**
   * Broadcasts an incoming SSE event to all subscribers listening for that event name.
   *
   * Automatically parses JSON payloads where applicable and safely catches subscriber handler errors.
   *
   * @private
   * @param {string} eventName - Name of the incoming SSE event (e.g. `'agent-update'`).
   * @param {MessageEvent} event - The raw MessageEvent object received from EventSource.
   */
  private broadcast(eventName: string, event: MessageEvent): void {
    let parsedData: any = null;
    try {
      parsedData = JSON.parse(event.data);
    } catch (e) {
      parsedData = event.data;
    }

    this.subscribers.forEach((subscriber) => {
      const handler = subscriber.eventHandlers[eventName];
      if (handler) {
        try {
          handler(parsedData, event);
        } catch (err) {
          console.error(`Error in SSE handler for ${eventName}:`, err);
        }
      }
    });
  }

  /**
   * Closes the active EventSource connection and clears all reconnect/disconnect timers.
   *
   * Does not clear the subscriber registry (used internally when grace period expires).
   */
  public disconnect(): void {
    if (this.disconnectTimeout) {
      clearTimeout(this.disconnectTimeout);
      this.disconnectTimeout = null;
    }
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this.connectionKey = null;
    this.reconnectAttempts = 0;
  }

  /**
   * Fully resets the SSE service by clearing all registered subscribers and immediately closing the stream.
   *
   * Typically called during user logout or full application cleanup.
   */
  public disconnectAll(): void {
    this.subscribers.clear();
    this.disconnect();
  }
}

/**
 * Shared singleton instance of the SSEManager.
 *
 * @type {SSEManager}
 */
export const sseService = new SSEManager();
